import {
  Document,
  Paragraph,
  TextRun,
  Packer,
  AlignmentType,
} from 'docx';
import moment from 'moment';
import { MONTH_GENITIVE, MONTH_GENITIVE_NEW } from '../../../../../assets/const';
import { getInitials } from '../../../../../utils/smallHelpers';

// ─── Утилиты ──────────────────────────────────────────────────────────────────

const createTextParagraph = (text, options = {}) => {
  const {
    bold = false,
    italic = false,
    size,
    alignment = AlignmentType.LEFT,
    indentLeft = 0,
    indentFirstLine = 0,
    spacingAfter = 300,
    font = 'Times New Roman',
    rightAligned = false,
  } = options;

  const safeText = text || '';

  const textRunOptions = {
    text: safeText,
    bold,
    italic,
    font: { name: font },
  };

  if (size != null && !isNaN(size) && size > 0 && size <= 100) {
    textRunOptions.size = size * 2;
  }

  const children = [new TextRun(textRunOptions)];

  if (rightAligned) {
    children.unshift(new TextRun({ text: '\t', font: { name: font } }));
  }

  const indent = {};
  if (indentLeft > 0) indent.left = indentLeft;
  if (indentFirstLine !== 0) indent.firstLine = indentFirstLine;

  return new Paragraph({
    children,
    alignment: rightAligned ? AlignmentType.RIGHT : alignment,
    indent: Object.keys(indent).length > 0 ? indent : undefined,
    spacing: { after: spacingAfter },
    tabStops: rightAligned ? [{ position: 9000 }] : undefined,
  });
};

const createJustifiedParagraph = (text, bold = false) =>
  createTextParagraph(text, { bold, alignment: AlignmentType.JUSTIFIED, indentFirstLine: 720 });

const createCenteredParagraph = (text, bold = true, size = 12) =>
  createTextParagraph(text, { bold, size, alignment: AlignmentType.CENTER });

const createPageBreak = () =>
  new Paragraph({
    children: [new TextRun({ text: '\u00A0', font: { name: 'Times New Roman' } })],
    pageBreakBefore: true,
    spacing: { before: 0, after: 0 },
  });

// ─── Основная функция ─────────────────────────────────────────────────────────

const getWordProtocol = async ({ data, comission, userInfo }) => {
  const { last_name, first_name, patronymic } = userInfo;
  const createrName = `${last_name || ''} ${first_name || ''} ${patronymic || ''}`;
  const dateTo = comission?.date_to;
  const formattedDate = moment(dateTo).format(`DD ${MONTH_GENITIVE[moment(dateTo).month()]} YYYY`);
  const formattedDateNew = moment(dateTo).format(`${MONTH_GENITIVE_NEW[moment(dateTo).month()]} YYYY`);

  const content = [];

  // === ЗАГОЛОВОК ===
  content.push(
    createCenteredParagraph('Протокол № ___', true, 13),
    createJustifiedParagraph('заседания экспертной группы Аттестационной комиссии УВО ГУК Министретства обороны Российской Федерации педагогических и руководящих работников организаций, осуществляющих образовательную деятельность и находящихся в ведении Министерства обороны Российской Федерации от'),
    createCenteredParagraph(`${formattedDate} года`, true, 13)
  );

  // === СОСТАВ ===
  content.push(createTextParagraph('Присутствовали:', true, { spacingAfter: 400 }));

  content.push(
    createTextParagraph(
      'Руководитель экспертной группы Н.А.Близнова – заведующий методическим кабинетом Московского суворовского военного ордена Почета училища.',
      { indentLeft: 720 }
    ),
    createTextParagraph('Члены экспертной группы:', true)
  );

  (data.experts || []).forEach((item) => {
    const fio = getInitials(`${item.LastName || ''} ${item.FirstName || ''} ${item.Patronymic || ''}`);
    content.push(createTextParagraph(`${fio} - ${item.doljnost || ''}`, { indentLeft: 720 }));
  });

  // === ПОВЕСТКА ===
  content.push(
    createCenteredParagraph('Повестка дня:', true, 12),
    createJustifiedParagraph(`1. Рассмотрение аттестационных материалов педагогических работников с целью установления первой квалификационной категории в ${formattedDateNew} года.`),
    createJustifiedParagraph(`2. Рассмотрение аттестационных материалов педагогических работников с целью установления высшей квалификационной категории в ${formattedDateNew} года.`)
  );

  // === ХОД ЗАСЕДАНИЯ ===
  content.push(
    createCenteredParagraph('Ход заседания:', true, 12),
    createJustifiedParagraph('1. Заслушали руководителя экспертной группы Н.А.Близнову по вопросу проведения аттестации педагогических работников организаций, осуществляющих образовательную деятельность и находящихся в ведении Министерства обороны Российской Федерации, с целью установления'),
    createTextParagraph('первой квалификационной категории.', true, { indentLeft: 720 }),
    createJustifiedParagraph('По результатам экспертизы представленных аттестационных материалов считать уровень квалификации следующих педагогических и руководящих работников соответствующим требованиям, предъявляемым к'),
    createTextParagraph('первой квалификационной категории:', true, { indentLeft: 720 })
  );

  if (data.reasons_statistic_first_category) {
    Object.keys(data.reasons_statistic_first_category).forEach((school) => {
      content.push(createCenteredParagraph(school, true, 12));
      (data.reasons_statistic_first_category[school] || []).forEach((item, ind) => {
        const fio = `${item.lastName || ''} ${item.firstName || ''} ${item.patronymic || ''}, ${item.position || ''}`;
        content.push(createTextParagraph(`${ind + 1}. ${fio}`, { indentLeft: 720 }));
      });
    });
  }

  content.push(
    createCenteredParagraph('Решение экспертной группы:', true, 12),
    createJustifiedParagraph('Рекомендовать установить первую квалификационную категорию вышеперечисленным педагогическим работникам организаций, осуществляющих образовательную деятельность и находящихся в ведении Министерства обороны Российской Федерации, по занимаемым должностям.'),
    createTextParagraph('Результаты голосования:', true),
    createTextParagraph('«за» – 9'),
    createTextParagraph('«против» – 0')
  );

  // === ВЫСШАЯ КАТЕГОРИЯ ===
  content.push(
    createJustifiedParagraph('2. Заслушали руководителя экспертной группы Н.А.Близнову по вопросу проведения аттестации педагогических работников организаций, осуществляющих образовательную деятельность и находящихся в ведении Министерства обороны Российской Федерации, с целью установления'),
    createTextParagraph('высшей квалификационной категории.', true, { indentLeft: 720 }),
    createJustifiedParagraph('По результатам экспертизы представленных аттестационных материалов считать уровень квалификации следующих педагогических и руководящих работников соответствующим требованиям, предъявляемым к'),
    createTextParagraph('высшей квалификационной категории:', true, { indentLeft: 720 })
  );

  if (data.reasons_statistic_highest_category) {
    Object.keys(data.reasons_statistic_highest_category).forEach((school) => {
      content.push(createCenteredParagraph(school, true, 12));
      (data.reasons_statistic_highest_category[school] || []).forEach((item, ind) => {
        const fio = `${item.lastName || ''} ${item.firstName || ''} ${item.patronymic || ''}, ${item.position || ''}`;
        content.push(createTextParagraph(`${ind + 1}. ${fio}`, { indentLeft: 720 }));
      });
    });
  }

  content.push(
    createCenteredParagraph('Решение экспертной группы:', true, 12),
    createJustifiedParagraph('Рекомендовать установить высшую квалификационную категорию вышеперечисленным педагогическим работникам организаций, осуществляющих образовательную деятельность и находящихся в ведении Министерства обороны Российской Федерации, по занимаемым должностям.'),
    createTextParagraph('Результаты голосования:', true, { spacingAfter: 600 }),
    createTextParagraph('«за» – 9'),
    createTextParagraph('«против» – 0')
  );

  // === ПОДПИСИ ===
  content.push(
    createTextParagraph('Руководитель экспертной группы:', { spacingAfter: 400 }),
    createTextParagraph('Н.А. Близнова', { rightAligned: true, spacingAfter: 600 }),
    createTextParagraph('Члены экспертной группы:', { spacingAfter: 400 })
  );

  (data.experts || []).forEach((item) => {
    const fio = getInitials(`${item.LastName || ''} ${item.FirstName || ''} ${item.Patronymic || ''}`);
    content.push(createTextParagraph(fio, { rightAligned: true, spacingAfter: 200 }));
  });

  // === ПОДВАЛ ===
  content.push(
    createPageBreak(),
    createTextParagraph(`Сформировано: ${createrName}`, { size: 8, alignment: AlignmentType.LEFT, spacingAfter: 50 }),
    createTextParagraph(`Дата: ${moment(dateTo).format('DD.MM.YYYY')}`, { size: 8, alignment: AlignmentType.LEFT, spacingAfter: 50 }),
    createTextParagraph('Документ сформирован в системе управления обучением', { size: 8, italic: true, alignment: AlignmentType.LEFT, spacingAfter: 100 })
  );

  const doc = new Document({
    styles: { default: { document: { run: { font: 'Times New Roman', size: 24 } } } },
    sections: [{ children: content }],
  });

  return Packer.toBlob(doc);
};

export default getWordProtocol;
